/*
 * Product Card Modal
 * Simple modal open/close functionality for product card delivery badges.
 */

/**
 * Closes a modal.
 * @param {HTMLElement} dialog - The modal dialog element.
 */
function closeModal(dialog) {
  const dialogElement = /** @type {HTMLElement} */ (dialog);
  document.body.classList.remove('overflow-hidden');
  dialogElement.removeAttribute('open');
}

/**
 * Handles Escape key globally to prevent carousel navigation when modal is open.
 * @param {KeyboardEvent} event - The keyboard event.
 */
function handleGlobalEscapeKey(event) {
  if (event.code !== 'Escape' && event.key !== 'Escape') return;

  const openModal = document.querySelector('.js-product-card-modal[open]');
  if (!openModal || !(openModal instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  closeModal(openModal);
}

/**
 * Initializes product card modals.
 * Made available globally for Tagalys integration.
 */
function initProductCardModals() {
  const modals = document.querySelectorAll('.js-product-card-modal');
  const openers = document.querySelectorAll('.product-card__delivery-badge-opener button');

  // Remove existing global listener if re-initializing.
  document.removeEventListener('keydown', handleGlobalEscapeKey, true);
  // Add global listener with capture to catch before Swiper.
  document.addEventListener('keydown', handleGlobalEscapeKey, true);

  // Set up open handlers.
  openers.forEach((showButton) => {
    const opener = showButton.closest('.product-card__delivery-badge-opener');
    if (!opener) return;

    const modalSelector = opener.getAttribute('data-modal');
    if (!modalSelector) return;

    const dialog = document.querySelector(modalSelector);
    if (!dialog) return;

    showButton.addEventListener('click', (event) => {
      // Prevent navigation when button is inside a link.
      event.preventDefault();
      event.stopPropagation();

      // Set HTML content if stored in data attribute (Tagalys escapes HTML in templates).
      const dialogElement = /** @type {HTMLElement} */ (dialog);
      const contentElement = dialog.querySelector('.product-card-modal__content-info');
      if (contentElement && dialogElement.dataset.deliveryBadgeContent) {
        // Parse HTML using DOMParser
        const parser = new DOMParser();
        const doc = parser.parseFromString(dialogElement.dataset.deliveryBadgeContent, 'text/html');
        const links = doc.querySelectorAll('a[href]');
        links.forEach((link) => {
          const anchorElement = /** @type {HTMLAnchorElement} */ (link);
          let href = anchorElement.getAttribute('href');
          if (!href) return;
          // Replace HTML entities that may be in the href value.
          href = href
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
          // Decode URL-encoded content.
          try {
            href = decodeURIComponent(href);
          } catch {
            // If decoding fails, continue with original href.
          }
          // Clean href: remove surrounding quotes (handles both regular and encoded quotes).
          href = href
            .trim()
            .replace(/^["']+|["']+$/g, '')
            .replace(/^%22+|%22+$/g, '');
          // Only process absolute URLs.
          if (href.startsWith('http://') || href.startsWith('https://')) {
            anchorElement.href = href;
          }
        });
        contentElement.innerHTML = doc.body.innerHTML;
      }

      document.body.classList.add('overflow-hidden');
      dialog.setAttribute('open', '');

      // Focus the dialog for keyboard accessibility.
      const dialogContent = dialog.querySelector('[role="dialog"]');
      if (dialogContent && dialogContent instanceof HTMLElement) {
        setTimeout(() => {
          dialogContent.focus();
        }, 0);
      }
    });
  });

  // Set up close handlers for each modal.
  modals.forEach((dialog) => {
    const closeButton = dialog.querySelector('[id^="ModalClose-"]');

    // Close button handler.
    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeModal(/** @type {HTMLElement} */ (dialog));
      });
    }

    // Click-away to close (backdrop click).
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        event.preventDefault();
        event.stopPropagation();
        closeModal(/** @type {HTMLElement} */ (dialog));
      }
    });
  });
}

// Initialize on DOM content loaded.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardModals);
} else {
  initProductCardModals();
}

// Re-initialize when new content is added dynamically.
document.addEventListener('shopify:section:load', initProductCardModals);

// Make function available globally for Tagalys integration.
window.initProductCardModals = initProductCardModals;
